// skipcq: JS-W1028
import PropTypes from 'prop-types';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';

function applyChangesButtonLabel(applying, applyRetryable) {
  if (applying) {
    return 'Applying…';
  }
  return applyRetryable ? 'Retry apply' : 'Apply approved changes';
}

function ApplyChangesButton({
  actionLoading,
  applying,
  applyRetryable,
  applyBlocked,
  unmappedStylesMessage,
  onApply,
}) {
  return (
    <Button
      variant="contained"
      onClick={onApply}
      disabled={actionLoading || Boolean(unmappedStylesMessage) || applyBlocked}
      aria-busy={applying || undefined}
      startIcon={applying ? <CircularProgress size={16} color="inherit" /> : undefined}
    >
      {applyChangesButtonLabel(applying, applyRetryable)}
    </Button>
  );
}

ApplyChangesButton.propTypes = {
  actionLoading: PropTypes.bool,
  applying: PropTypes.bool,
  applyRetryable: PropTypes.bool,
  applyBlocked: PropTypes.bool,
  unmappedStylesMessage: PropTypes.string,
  onApply: PropTypes.func.isRequired,
};

function completedApplyMessage(unmappedStylesMessage, applyRetryable) {
  if (unmappedStylesMessage) {
    return unmappedStylesMessage;
  }
  if (applyRetryable) {
    return 'Apply did not finish. Retry apply to publish remaining approved beers.';
  }
  return 'Review complete. Apply approved changes to publish beers through the catalog import path.';
}

function CompletedReviewFooter({
  reviewApplied,
  actionLoading,
  applying,
  applyRetryable,
  applyBlocked,
  unmappedStylesMessage,
  onApply,
}) {
  if (reviewApplied) {
    return (
      <Alert severity="success">
        Approved changes were applied to the catalog.
      </Alert>
    );
  }
  return (
    <>
      <Alert severity={unmappedStylesMessage ? 'warning' : 'info'} sx={{ mb: 1.5 }}>
        {completedApplyMessage(unmappedStylesMessage, applyRetryable)}
      </Alert>
      <ApplyChangesButton
        actionLoading={actionLoading}
        applying={applying}
        applyRetryable={applyRetryable}
        applyBlocked={applyBlocked}
        unmappedStylesMessage={unmappedStylesMessage}
        onApply={onApply}
      />
    </>
  );
}

CompletedReviewFooter.propTypes = {
  reviewApplied: PropTypes.bool,
  actionLoading: PropTypes.bool,
  applying: PropTypes.bool,
  applyRetryable: PropTypes.bool,
  applyBlocked: PropTypes.bool,
  unmappedStylesMessage: PropTypes.string,
  onApply: PropTypes.func.isRequired,
};

function OpenReviewFooter({
  pendingCount,
  selectedCount,
  bulkEligibleCount,
  selectedBulkEligibleCount,
  selectedRejectableCount,
  actionLoading,
  applying,
  applyRetryable,
  applyBlocked,
  unmappedStylesMessage,
  onSelectEligible,
  onBulkApprove,
  onBulkReject,
  onComplete,
  onApply,
}) {
  const showApply = pendingCount === 0;
  return (
    <>
      {unmappedStylesMessage ? (
        <Alert severity="warning" sx={{ width: '100%', mb: 1 }}>
          {unmappedStylesMessage}
        </Alert>
      ) : null}
      <Typography variant="body2">
        {selectedCount} selected · {bulkEligibleCount} eligible
      </Typography>
      <Button size="small" onClick={onSelectEligible} disabled={actionLoading}>
        Select eligible
      </Button>
      <Button
        size="small"
        variant="contained"
        onClick={onBulkApprove}
        disabled={actionLoading || selectedBulkEligibleCount === 0}
      >
        Approve selected
      </Button>
      <Button
        size="small"
        variant="outlined"
        onClick={onBulkReject}
        disabled={actionLoading || selectedRejectableCount === 0}
      >
        Reject selected
      </Button>
      {pendingCount === 0 ? (
        <Button
          size="small"
          onClick={onComplete}
          disabled={actionLoading || Boolean(unmappedStylesMessage)}
        >
          Complete review
        </Button>
      ) : null}
      {showApply ? (
        <ApplyChangesButton
          actionLoading={actionLoading}
          applying={applying}
          applyRetryable={applyRetryable}
          applyBlocked={applyBlocked}
          unmappedStylesMessage={unmappedStylesMessage}
          onApply={onApply}
        />
      ) : null}
    </>
  );
}

OpenReviewFooter.propTypes = {
  pendingCount: PropTypes.number.isRequired,
  selectedCount: PropTypes.number.isRequired,
  bulkEligibleCount: PropTypes.number.isRequired,
  selectedBulkEligibleCount: PropTypes.number.isRequired,
  selectedRejectableCount: PropTypes.number.isRequired,
  actionLoading: PropTypes.bool,
  applying: PropTypes.bool,
  applyRetryable: PropTypes.bool,
  applyBlocked: PropTypes.bool,
  unmappedStylesMessage: PropTypes.string,
  onSelectEligible: PropTypes.func.isRequired,
  onBulkApprove: PropTypes.func.isRequired,
  onBulkReject: PropTypes.func.isRequired,
  onComplete: PropTypes.func.isRequired,
  onApply: PropTypes.func.isRequired,
};

const STICKY_FOOTER_SX = {
  position: 'sticky',
  bottom: 0,
  bgcolor: 'background.paper',
  borderTop: 1,
  borderColor: 'divider',
  py: 1.5,
  mt: 2,
  display: 'flex',
  gap: 1,
  flexWrap: 'wrap',
  alignItems: 'center',
};

export default function BeerIngestionReviewFooter({
  pendingCount,
  selectedCount = 0,
  bulkEligibleCount,
  selectedBulkEligibleCount = 0,
  selectedRejectableCount = 0,
  reviewLocked,
  reviewApplied,
  actionLoading,
  applying = false,
  applyRetryable = false,
  applyBlocked = false,
  unmappedStylesMessage,
  onSelectEligible,
  onBulkApprove,
  onBulkReject,
  onComplete,
  onApply,
}) {
  if (reviewApplied || (reviewLocked && pendingCount === 0)) {
    return (
      <Box sx={{ mt: 2 }}>
        <CompletedReviewFooter
          reviewApplied={reviewApplied}
          actionLoading={actionLoading}
          applying={applying}
          applyRetryable={applyRetryable}
          applyBlocked={applyBlocked}
          unmappedStylesMessage={unmappedStylesMessage}
          onApply={onApply}
        />
      </Box>
    );
  }
  return (
    <Box sx={STICKY_FOOTER_SX}>
      <OpenReviewFooter
        pendingCount={pendingCount}
        selectedCount={selectedCount}
        bulkEligibleCount={bulkEligibleCount}
        selectedBulkEligibleCount={selectedBulkEligibleCount}
        selectedRejectableCount={selectedRejectableCount}
        actionLoading={actionLoading}
        applying={applying}
        applyRetryable={applyRetryable}
        applyBlocked={applyBlocked}
        unmappedStylesMessage={unmappedStylesMessage}
        onSelectEligible={onSelectEligible}
        onBulkApprove={onBulkApprove}
        onBulkReject={onBulkReject}
        onComplete={onComplete}
        onApply={onApply}
      />
    </Box>
  );
}

BeerIngestionReviewFooter.propTypes = {
  pendingCount: PropTypes.number.isRequired,
  selectedCount: PropTypes.number,
  bulkEligibleCount: PropTypes.number.isRequired,
  selectedBulkEligibleCount: PropTypes.number,
  selectedRejectableCount: PropTypes.number,
  reviewLocked: PropTypes.bool,
  reviewApplied: PropTypes.bool,
  actionLoading: PropTypes.bool,
  applying: PropTypes.bool,
  applyRetryable: PropTypes.bool,
  applyBlocked: PropTypes.bool,
  unmappedStylesMessage: PropTypes.string,
  onSelectEligible: PropTypes.func.isRequired,
  onBulkApprove: PropTypes.func.isRequired,
  onBulkReject: PropTypes.func.isRequired,
  onComplete: PropTypes.func.isRequired,
  onApply: PropTypes.func.isRequired,
};
